/**
 * Landing-hero Map3D camera + spherical wind helpers.
 * Particles live on lat/lng so they wrap the globe instead of a flat canvas.
 */
export const LANDING_HERO_GLOBE_CENTER = { lat: 31.35, lng: -99.9, altitude: 0 };
export const LANDING_HERO_GLOBE_RANGE = 4_500_000;
export const LANDING_HERO_GLOBE_TILT = 26;
export const LANDING_HERO_GLOBE_HEADING = 0;

/** Landing rail −/+ dispatch this; the hero globe listens and changes Map3D range. */
export const LANDING_GLOBE_ZOOM_EVENT = 'zwn-landing-globe-zoom';
/** Same factors as the globe HUD + / − cluster. */
export const LANDING_GLOBE_ZOOM_IN_FACTOR = 0.55;
export const LANDING_GLOBE_ZOOM_OUT_FACTOR = 1.8;

export function dispatchLandingGlobeZoom(factor: number): void {
  if (typeof window === 'undefined') return;
  if (!Number.isFinite(factor) || factor <= 0) return;
  window.dispatchEvent(new CustomEvent(LANDING_GLOBE_ZOOM_EVENT, { detail: { factor } }));
}

export const EARTH_RADIUS_M = 6_371_000;
const LANDING_GLOBE_FOVY_DEG = 28;

export type LandingGlobeCamera = {
  lat: number;
  lng: number;
  range: number;
  tilt: number;
  heading: number;
};

type Vec3 = { x: number; y: number; z: number };

const add = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
const scale = (a: Vec3, s: number): Vec3 => ({ x: a.x * s, y: a.y * s, z: a.z * s });
const dot = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;
const cross = (a: Vec3, b: Vec3): Vec3 => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
});
const len = (a: Vec3): number => Math.hypot(a.x, a.y, a.z);
const norm = (a: Vec3): Vec3 => {
  const l = len(a) || 1;
  return scale(a, 1 / l);
};

export function wrapGlobeLng(lng: number): number {
  let x = lng;
  while (x > 180) x -= 360;
  while (x < -180) x += 360;
  return x;
}

export function latLngToEcef(lat: number, lng: number, radius = EARTH_RADIUS_M): Vec3 {
  const la = (lat * Math.PI) / 180;
  const ln = (lng * Math.PI) / 180;
  const cl = Math.cos(la);
  return {
    x: radius * cl * Math.cos(ln),
    y: radius * cl * Math.sin(ln),
    z: radius * Math.sin(la),
  };
}

function ecefEast(lng: number): Vec3 {
  const ln = (lng * Math.PI) / 180;
  return { x: -Math.sin(ln), y: Math.cos(ln), z: 0 };
}

function ecefNorth(lat: number, lng: number): Vec3 {
  const la = (lat * Math.PI) / 180;
  const ln = (lng * Math.PI) / 180;
  return {
    x: -Math.sin(la) * Math.cos(ln),
    y: -Math.sin(la) * Math.sin(ln),
    z: Math.cos(la),
  };
}

function ecefUp(lat: number, lng: number): Vec3 {
  return latLngToEcef(lat, lng, 1);
}

export function defaultLandingGlobeCamera(): LandingGlobeCamera {
  return {
    lat: LANDING_HERO_GLOBE_CENTER.lat,
    lng: LANDING_HERO_GLOBE_CENTER.lng,
    range: LANDING_HERO_GLOBE_RANGE,
    tilt: LANDING_HERO_GLOBE_TILT,
    heading: LANDING_HERO_GLOBE_HEADING,
  };
}

function readFinite(value: unknown, fallback: number): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/** Live Map3D camera when the landing globe is mounted; else the hero defaults. */
export function readLiveLandingGlobeCamera(): LandingGlobeCamera {
  const fallback = defaultLandingGlobeCamera();
  if (typeof document === 'undefined') return fallback;
  const el = document.querySelector('[data-testid="google-3d-map"]') as
    | (HTMLElement & {
      center?: { lat?: number; lng?: number };
      range?: number;
      tilt?: number;
      heading?: number;
    })
    | null;
  if (!el) return fallback;
  const center = el.center || {};
  return {
    lat: readFinite(center.lat, fallback.lat),
    lng: readFinite(center.lng, fallback.lng),
    range: Math.max(800, readFinite(el.range, fallback.range)),
    tilt: readFinite(el.tilt, fallback.tilt),
    heading: readFinite(el.heading, fallback.heading),
  };
}

function cameraEcef(cam: LandingGlobeCamera): { camera: Vec3; lookAt: Vec3 } {
  const lookAt = latLngToEcef(cam.lat, cam.lng);
  const tiltR = (cam.tilt * Math.PI) / 180;
  const headR = (cam.heading * Math.PI) / 180;
  const up = cam.range * Math.cos(tiltR);
  const north = -cam.range * Math.sin(tiltR) * Math.cos(headR);
  const east = -cam.range * Math.sin(tiltR) * Math.sin(headR);
  const camera = add(
    lookAt,
    add(
      scale(ecefEast(cam.lng), east),
      add(scale(ecefNorth(cam.lat, cam.lng), north), scale(ecefUp(cam.lat, cam.lng), up)),
    ),
  );
  return { camera, lookAt };
}

export function isLandingGlobeFrontFace(lat: number, lng: number, cam: LandingGlobeCamera): boolean {
  const p = latLngToEcef(lat, lng);
  const { camera } = cameraEcef(cam);
  return dot(p, camera) >= EARTH_RADIUS_M * EARTH_RADIUS_M * 0.995;
}

export function projectLandingGlobePoint(
  lat: number,
  lng: number,
  width: number,
  height: number,
  cam: LandingGlobeCamera,
): { x: number; y: number; visible: boolean } {
  const p = latLngToEcef(lat, lng);
  const { camera, lookAt } = cameraEcef(cam);
  const visible = dot(p, camera) >= EARTH_RADIUS_M * EARTH_RADIUS_M * 0.995;
  const forward = norm({ x: lookAt.x - camera.x, y: lookAt.y - camera.y, z: lookAt.z - camera.z });
  const upGuess = ecefUp(cam.lat, cam.lng);
  let right = cross(forward, upGuess);
  if (len(right) < 1e-8) right = ecefEast(cam.lng);
  right = norm(right);
  const up = norm(cross(right, forward));
  const rel = { x: p.x - camera.x, y: p.y - camera.y, z: p.z - camera.z };
  const zCam = dot(rel, forward);
  if (zCam <= 20) return { x: width / 2, y: height / 2, visible: false };
  const xCam = dot(rel, right);
  const yCam = dot(rel, up);
  const f = 1 / Math.tan(((LANDING_GLOBE_FOVY_DEG * Math.PI) / 180) / 2);
  const aspect = width > 0 ? width / Math.max(1, height) : 1;
  const ndcX = (xCam / zCam) * (f / aspect);
  const ndcY = (yCam / zCam) * f;
  const x = (ndcX + 1) * 0.5 * width;
  const y = (1 - ndcY) * 0.5 * height;
  const onScreen = x >= -40 && x <= width + 40 && y >= -40 && y <= height + 40;
  return { x, y, visible: visible && onScreen && zCam > 20 };
}

/**
 * Wind field on the sphere. Permian sample is the base heading; curl varies
 * with lat/lng so streaks wrap the globe instead of one parallel.
 */
export function globeFlowAtLatLng(
  lat: number,
  lng: number,
  sample: { speed: number; direction: number },
): { east: number; north: number; speed: number } {
  const windRadians = ((sample.direction + 90) * Math.PI) / 180;
  let east = Math.cos(windRadians);
  let south = Math.sin(windRadians);
  const ln = (lng * Math.PI) / 180;
  const la = (lat * Math.PI) / 180;
  const curl = 0.95 * Math.sin(ln * 3.1 + la * 2.4)
    + 0.55 * Math.sin(ln * 5.6 - la * 4.1)
    + (lat / 90) * 0.35;
  const ca = Math.cos(curl);
  const sa = Math.sin(curl);
  const rx = east * ca - south * sa;
  const ry = east * sa + south * ca;
  const speedMul = 0.7 + 0.55 * (0.5 + 0.5 * Math.sin(ln * 2.7 + la * 3.3));
  const speed = Math.max(2, sample.speed * speedMul);
  return { east: rx, north: -ry, speed };
}

export function advectGlobeLatLng(
  lat: number,
  lng: number,
  eastM: number,
  northM: number,
): { lat: number; lng: number } {
  const latR = (lat * Math.PI) / 180;
  const cosLat = Math.max(0.08, Math.cos(latR));
  const nextLat = Math.max(-80, Math.min(80, lat + (northM / EARTH_RADIUS_M) * (180 / Math.PI)));
  const nextLng = wrapGlobeLng(lng + (eastM / (EARTH_RADIUS_M * cosLat)) * (180 / Math.PI));
  return { lat: nextLat, lng: nextLng };
}

export function landingGlobeMetersPerPixel(height: number, cam: LandingGlobeCamera): number {
  const fovy = (LANDING_GLOBE_FOVY_DEG * Math.PI) / 180;
  return (2 * cam.range * Math.tan(fovy / 2)) / Math.max(1, height);
}
